import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import { buildCustomerLedgerPdfHeader } from '../../utils/customerLedgerPdf';
import { LOGIN_COMPANY_DISPLAY_NAME } from '../../utils/loginCompany';
import { exportFormattedExcelReport } from '../../utils/formattedExcelExport';

export function formatSiteDetailCellValue(value, key) {
  if (value === null || value === undefined || value === '') {
    return key === 'amount' ? '-' : '';
  }
  if (key === 'quantity' || key === 'amount' || key === 'rate') {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value;
  }
  return value;
}

const DETAIL_TYPE_FILENAME = {
  quotation: 'Quotation',
  order: 'SalesOrder',
  schedule: 'Schedule',
  dc: 'DC',
};

function shortenFilenamePart(value, maxLength = 20) {
  if (!value) {
    return '';
  }
  const cleaned = String(value)
    .replace(/[<>:"/\\|?*]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const siteNameOnly = cleaned.split(',')[0].trim() || cleaned;
  return siteNameOnly.length > maxLength
    ? siteNameOnly.slice(0, maxLength).trim()
    : siteNameOnly;
}

function sanitizeExportFilename(detailType, siteLabel) {
  const typeCode = DETAIL_TYPE_FILENAME[detailType] || 'SiteDetails';
  const sitePart = shortenFilenamePart(siteLabel, 20);
  const base = sitePart ? `${typeCode}-${sitePart}` : typeCode;
  return base.replace(/\s+/g, '_').slice(0, 32) || 'site-details';
}

function buildExportSummaryLines(customerName, siteLabel, rowCount) {
  const lines = [];
  if (customerName) {
    lines.push(`Customer Name: ${customerName}`);
  }
  if (siteLabel) {
    lines.push(`Site: ${siteLabel}`);
  }
  lines.push(`Total Records: ${rowCount}`);
  return lines;
}

async function buildExportHeader(cookies, title) {
  if (cookies) {
    try {
      return await buildCustomerLedgerPdfHeader(cookies, [], [], {
        documentTitle: title,
        subtitle: '',
      });
    } catch (error) {
      // Fall back to a simple branded header.
    }
  }
  return {
    logoDataUrl: null,
    companyName: LOGIN_COMPANY_DISPLAY_NAME,
    addressLines: [],
    gstNo: '',
    documentTitle: title,
    subtitle: '',
  };
}

function buildExportColumns(columns) {
  return columns.map((column) => ({
    key: column.key,
    label: column.label,
    align: column.align === 'end' ? 'right' : column.align === 'center' ? 'center' : 'left',
  }));
}

export async function exportCustomerSiteDetailExcel({
  cookies,
  columns,
  rows,
  title,
  detailType,
  customerName,
  siteLabel,
}) {
  if (!columns?.length || !rows?.length) {
    return;
  }
  const exportColumns = buildExportColumns(columns);
  const header = await buildExportHeader(cookies, title);
  await exportFormattedExcelReport({
    filename: sanitizeExportFilename(detailType, siteLabel),
    documentTitle: title,
    infoLines: buildExportSummaryLines(customerName, siteLabel, rows.length),
    columns: exportColumns,
    rows,
    formatCellValue: formatSiteDetailCellValue,
    totalKeys: ['quantity', 'amount'],
    header: {
      companyName: header.companyName,
      addressLines: header.addressLines,
      gstNo: header.gstNo,
      logoDataUrl: header.logoDataUrl,
    },
  });
}

export async function exportCustomerSiteDetailPDF({
  cookies,
  columns,
  rows,
  title,
  detailType,
  customerName,
  siteLabel,
}) {
  if (!columns?.length || !rows?.length) {
    return;
  }
  const exportColumns = buildExportColumns(columns).map((column) => ({
    header: column.label,
    dataKey: column.key,
    align: column.align,
    halign: column.align,
  }));
  const exportRows = rows.map((row) => {
    const record = {};
    columns.forEach((column) => {
      record[column.key] = formatSiteDetailCellValue(row[column.key], column.key);
    });
    return record;
  });
  const summaryLines = buildExportSummaryLines(customerName, siteLabel, rows.length);
  const header = await buildExportHeader(cookies, title);
  const blob = createPdfBlob(title, exportColumns, exportRows, summaryLines, header);
  openPdfInApp(blob, {
    title,
    downloadFileName: `${sanitizeExportFilename(detailType, siteLabel)}.pdf`,
  });
}
